import { Link } from "react-router-dom";
import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { Sparkles, CheckCircle2, Cloud, Cpu, MessageCircle, Linkedin, Mail, Database, Activity, ArrowRight } from 'lucide-react';
import { Logo } from "@/components/common/Logo";

// Phase 11c — Public-facing status page. No auth.

const SERVICES = [
  { id: "api",      Icon: Cloud,         label: "Sellyx API",                uptime: "99.97" },
  { id: "openclaw", Icon: Sparkles,      label: "OpenClaw Gateway",          uptime: "99.99" },
  { id: "whatsapp", Icon: MessageCircle, label: "WhatsApp Channel",          uptime: "99.95" },
  { id: "linkedin", Icon: Linkedin,      label: "LinkedIn Channel",          uptime: "99.92" },
  { id: "gmail",    Icon: Mail,          label: "Gmail Channel",             uptime: "99.98" },
  { id: "vector",   Icon: Database,      label: "Vector Store · Pinecone",   uptime: "99.99" },
  { id: "model",    Icon: Cpu,           label: "GPT-5.5 Runtime",           uptime: "99.94" },
];

const INCIDENTS = [
  { date: "Feb 09, 2026", title: "Brief LinkedIn channel degradation",  body: "LinkedIn login attempts saw elevated retries for ~22 minutes due to upstream session-pool throttling. Resolved at 14:38 UTC. No data loss.",   resolved: true },
  { date: "Jan 27, 2026", title: "Gmail OAuth refresh delays",          body: "Google's OAuth token-refresh latency caused ~3 min of delayed deliveries on the Gmail channel. Resolved automatically.",                       resolved: true },
  { date: "Jan 14, 2026", title: "Scheduled vector-store maintenance",  body: "Pinecone us-east-1 underwent maintenance Sat 02:00-02:14 UTC as announced. Maya queued reads; no customer impact.",                              resolved: true },
];

export default function StatusPage() {
  const [email, setEmail] = useState("");
  return (
    <div className="bg-white min-h-screen">
      <nav className="sticky top-0 z-20 bg-white/85 backdrop-blur border-b border-slate-200/70">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 h-14 flex items-center gap-2 sm:gap-4 min-w-0">
          <Link to="/" className="shrink-0">
            <Logo size={22} />
          </Link>
          <span className="text-[12px] text-slate-400">·</span>
          <span className="text-[12.5px] font-medium text-slate-600 shrink-0">Status</span>
          <div className="ml-auto inline-flex items-center gap-0.5 sm:gap-2">
            <Link to="/demo" className="hidden sm:inline text-[12.5px] text-slate-600 hover:text-slate-900 px-2.5 py-1.5">Demo</Link>
            <Link to="/login" className="text-[12.5px] text-slate-600 hover:text-slate-900 px-2 sm:px-2.5 py-1.5 min-h-9 inline-flex items-center">Sign in</Link>
          </div>
        </div>
      </nav>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 py-8 sm:py-12 min-w-0">
        {/* Hero — all operational */}
        <div className="rounded-3xl bg-gradient-to-br from-emerald-50 via-white to-emerald-50/40 border border-emerald-200 p-5 sm:p-8 text-center" data-testid="status-hero">
          <div className="inline-flex h-14 w-14 items-center justify-center rounded-full bg-emerald-100"><CheckCircle2 size={26} className="text-emerald-600" /></div>
          <h1 className="mt-4 text-[26px] sm:text-[34px] md:text-[40px] font-semibold tracking-tight text-slate-900 leading-tight">All systems operational</h1>
          <p className="mt-2 text-[13.5px] text-slate-600">Last checked just now · all 7 services healthy across all regions.</p>
          <div className="mt-3 inline-flex items-center gap-1.5 rounded-full bg-white ring-1 ring-emerald-200 px-2.5 py-1 text-[11px] font-semibold text-emerald-700">
            <span className="h-1.5 w-1.5 rounded-full bg-emerald-500 animate-pulse" />
            {"Live monitor · 60s polling"}
          </div>
        </div>

        {/* Service rows */}
        <section className="mt-8">
          <h2 className="text-[18px] font-semibold tracking-tight text-slate-900 mb-3">Services</h2>
          <div className="rounded-2xl border border-slate-200 bg-white divide-y divide-slate-100 overflow-hidden">
            {SERVICES.map((s) => (
              <div key={s.id} className="px-3 sm:px-4 py-3 flex items-center gap-2 sm:gap-3 min-w-0" data-testid={`status-svc-${s.id}`}>
                <span className="h-8 w-8 shrink-0 rounded-md bg-slate-50 text-slate-700 flex items-center justify-center"><s.Icon size={14} /></span>
                <div className="flex-1 min-w-0 text-[13px] font-medium text-slate-900 truncate">{s.label}</div>
                <span className="inline-flex shrink-0 items-center gap-1 rounded-full bg-emerald-50 text-emerald-700 ring-1 ring-emerald-200 px-2 py-0.5 text-[10.5px] font-semibold">
                  <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" />
                  <span className="hidden sm:inline">Operational</span>
                  <span className="sm:hidden">OK</span>
                </span>
                <span className="hidden md:inline text-[11px] text-slate-500 tabular-nums w-24 text-right">{s.uptime}% · 90d</span>
              </div>
            ))}
          </div>
        </section>

        {/* Recent incidents */}
        <section className="mt-8">
          <h2 className="text-[18px] font-semibold tracking-tight text-slate-900 mb-3 inline-flex items-center gap-1.5"><Activity size={14} className="text-slate-600" /> Recent incidents</h2>
          <div className="space-y-2">
            {INCIDENTS.map((i) => (
              <div key={i.title} className="rounded-xl border border-slate-200 bg-white p-3" data-testid={`status-incident-${i.title.slice(0,10)}`}>
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-[10.5px] uppercase tracking-wider text-slate-400 font-semibold">{i.date}</span>
                  <div className="text-[13px] font-semibold text-slate-900">{i.title}</div>
                  <span className="ml-auto inline-flex items-center gap-1 rounded-full bg-emerald-50 text-emerald-700 ring-1 ring-emerald-200 px-1.5 py-0.5 text-[10px] font-semibold"><CheckCircle2 size={10} /> Resolved</span>
                </div>
                <div className="mt-1 text-[11.5px] text-slate-600 leading-relaxed">{i.body}</div>
              </div>
            ))}
          </div>
        </section>

        {/* Subscribe */}
        <section className="mt-8 rounded-2xl border border-slate-200 bg-slate-50/40 p-5">
          <div className="text-[13px] font-semibold text-slate-900">Subscribe to status updates</div>
          <p className="text-[11.5px] text-slate-500 mt-0.5">We&apos;ll email you about incidents and scheduled maintenance.</p>
          <form
            className="mt-3 flex flex-col sm:flex-row items-stretch sm:items-center gap-2 max-w-md"
            onSubmit={(e) => {
              e.preventDefault();
              if (!/\S+@\S+\.\S+/.test(email)) {
                toast.error("Enter a valid email");
                return;
              }
              toast.success("Subscribed · we'll keep you posted");
              setEmail("");
            }}
          >
            <Input type="email" placeholder="you@company.com" value={email} onChange={(e) => setEmail(e.target.value)} className="h-10 sm:h-9 text-[12.5px] min-w-0 flex-1" data-testid="status-subscribe-email" />
            <Button size="sm" type="submit" className="h-10 sm:h-9 text-[12px] shrink-0" data-testid="status-subscribe-submit">Subscribe <ArrowRight size={12} /></Button>
          </form>
        </section>

        <div className="mt-10 text-center text-[11px] text-slate-400">
          Powered by <a href="https://openclaw.ai" target="_blank" rel="noreferrer noopener" className="font-mono text-indigo-700 hover:text-indigo-800">OpenClaw</a> · <Link to="/" className="hover:text-slate-700">Back to Sellyx →</Link>
        </div>
      </div>
    </div>
  );
}
